/**
 * Phase 15.6P Batch B — Device Image Uploader.
 *
 * Mobile-first, upload-only image picker with live preview. Replaces every
 * "paste a URL" cover-image input on the platform.
 */
import { useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const ASPECT_MAP = {
  square: "aspect-square",
  wide: "aspect-[16/9]",
  banner: "aspect-[3/1]",
  cover: "aspect-[4/5]",
  portrait: "aspect-[3/4]",
};

const ImageUploader = ({
  value,
  onChange,
  onClear,
  purpose = "general",
  label = "Upload image",
  aspect = "square",
  required = false,
  disabled = false,
  helperText = "JPG or PNG, max 10 MB.",
  testIdPrefix = "image-uploader",
  className = "",
  maxSizeMB = 10,
}) => {
  const inputRef = useRef(null);
  const blobRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [localPreview, setLocalPreview] = useState(null);

  // Derived preview: local optimistic blob wins until upload returns the real URL.
  const previewUrl = localPreview || value || null;

  const revokeBlob = () => {
    if (blobRef.current) {
      URL.revokeObjectURL(blobRef.current);
      blobRef.current = null;
    }
  };

  const handleFile = async (file) => {
    if (!file) return;

    if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) {
      toast.error("Please choose a JPG, PNG, or WebP image");
      return;
    }
    if (file.size > maxSizeMB * 1024 * 1024) {
      toast.error(`Image too large (max ${maxSizeMB} MB)`);
      return;
    }

    revokeBlob();
    const blobUrl = URL.createObjectURL(file);
    blobRef.current = blobUrl;
    setLocalPreview(blobUrl);
    setUploading(true);
    setProgress(0);

    try {
      const form = new FormData();
      form.append("file", file);
      form.append("purpose", purpose);
      const res = await api.post("/upload/image", form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      const url = res.data?.url;
      if (!url) throw new Error("Upload returned no URL");
      onChange?.(url);
      revokeBlob();
      setLocalPreview(null); // Now parent has the hosted url; let it drive preview
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Upload failed");
      revokeBlob();
      setLocalPreview(null);
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const handleClick = () => {
    if (disabled || uploading) return;
    inputRef.current?.click();
  };

  const handleClear = (e) => {
    e.stopPropagation();
    revokeBlob();
    setLocalPreview(null);
    onChange?.("");
    onClear?.();
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleDrop = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (disabled || uploading) return;
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  const aspectClass = ASPECT_MAP[aspect] || ASPECT_MAP.square;

  return (
    <div className={className} data-testid={`${testIdPrefix}-root`}>
      {label && (
        <label className="block text-sm font-semibold text-purple-200 mb-2">
          {label}{required && <span className="text-pink-400 ml-1">*</span>}
        </label>
      )}

      <div
        onClick={handleClick}
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onDrop={handleDrop}
        className={`relative ${aspectClass} w-full rounded-2xl border-2 border-dashed transition-all overflow-hidden cursor-pointer select-none ${
          uploading ? "border-purple-400/60 bg-purple-500/5" :
          previewUrl ? "border-purple-500/40 bg-black/40 hover:border-purple-500/70" :
          "border-purple-500/30 bg-gradient-to-br from-purple-900/10 via-fuchsia-900/5 to-black hover:border-purple-500/60"
        } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
        data-testid={`${testIdPrefix}-dropzone`}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !disabled) { e.preventDefault(); handleClick(); } }}
      >
        {previewUrl ? (
          <>
            <img
              src={previewUrl}
              alt="Selected"
              className="w-full h-full object-cover"
              draggable={false}
              data-testid={`${testIdPrefix}-preview`}
            />
            <div className="absolute inset-0 bg-black/0 hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 hover:opacity-100">
              <span className="text-white font-semibold text-sm bg-purple-600/90 px-3 py-1.5 rounded-full">
                {uploading ? "Uploading…" : "Tap to change"}
              </span>
            </div>
            {!uploading && !disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 hover:bg-rose-600 text-white flex items-center justify-center text-sm border border-white/20 transition-colors"
                aria-label="Remove image"
                data-testid={`${testIdPrefix}-clear`}
              >
                ✕
              </button>
            )}
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <div className="text-5xl mb-2 opacity-70">📷</div>
            <p className="text-white font-bold text-base">Tap to upload from device</p>
            <p className="text-gray-400 text-xs mt-1 max-w-xs">{helperText}</p>
            <p className="text-purple-300/70 text-xs mt-2 hidden sm:block">or drag &amp; drop</p>
          </div>
        )}

        {uploading && (
          <div className="absolute inset-x-0 bottom-0 bg-black/85 px-3 py-2" data-testid={`${testIdPrefix}-progress`}>
            <div className="flex items-center gap-3">
              <div className="flex-1 h-1.5 rounded-full bg-purple-900/40 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all" style={{ width: `${progress}%` }} />
              </div>
              <span className="text-xs text-purple-100 font-bold tabular-nums">{progress}%</span>
            </div>
          </div>
        )}
      </div>

      {/*
        NOTE: `capture` is intentionally omitted. Setting capture="environment"
        forces mobile devices to open the rear camera ONLY, hiding the
        Photo Library / Files options. Leaving it off restores the native
        Apple/Android picker sheet (Take Photo · Photo Library · Choose File).
      */}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        disabled={disabled || uploading}
        data-testid={`${testIdPrefix}-input`}
      />
    </div>
  );
};

export default ImageUploader;
